// in src/App.js
import React from 'react';
import { Admin, TextField } from 'react-admin';
import polyglotI18nProvider from 'ra-i18n-polyglot';

import dataProvider from './core/providers/IccUserDataProvider';
import authProvider from './core/providers/authProvider';

import SolsticeLayout from './core/components/SolsticeLayout';
import { SolsticeTheme } from './core/themes/SolsticeTheme';
import InvStructureIcon from './core/themes/icons/InvStructureIcon';
import { TagIcon } from './core/themes/icons/TagIcon';
import AlternateEmailIcon from '@material-ui/icons/AlternateEmail';
import SettingsIcon from '@material-ui/icons/Settings';
import TermsIcon from '@material-ui/icons/Create';
import AlternateEmail from '@material-ui/icons/AlternateEmail';
import Login from './core/components/LoginPage';

import users from './features/users';
import funds from './features/funds';
import investors from './features/investors';
import resourceuploads from './features/resourceuploads';
import categories from './features/categories';
import presets from './features/presets';
import groups from './features/groups';
import documenttypes from './features/documenttypes';
import mailtemplates from './features/mailtemplates';
import splitmethods from './features/splitmethods';
import tenantconfigurations from './features/tenantconfigurations';
import organizations from './features/organizations';
import fundfamilies from './features/fundfamilies';
import documentwatermarks from './features/documentwatermarks';
import roles from './features/roles';
import fundtags from './features/fundtags';
import investortags from './features/investortags';
import fundinvestortags from './features/fundinvestortags';
import emailarchive from './features/emailarchives';
import categoryterms from './features/categoryterms';
import fundterms from './features/fundterms';
import distributionlists from './features/distributionlists';
import reports from './features/reports';

import './core/themes/solstice.css';

import englishMessages from './core/i18n/englishLanguageMessages';
import SolsticeLogoutButton from './core/components/SolsticeLogoutButton';
import { runningInPortal } from './core/utils/IcxPortal';
import { isDevMode } from './core/utils';
import Resource from './core/components/forked/core/Resource';
import NotActivatedMessage from './NotActivatedMessage';

import profileReducer from './core/providers/customResources/profile/profileReducer';
import profileSaga from './core/providers/customResources/profile/profileSaga';

const Empty = () => <div></div>;
//disable the right click menu
if (!isDevMode()) {
  document.addEventListener('contextmenu', event => event.preventDefault());
}

// I18N customization
const messages = {
  en: englishMessages
};
const i18nProvider = polyglotI18nProvider(locale => messages[locale], 'en');

// Ugly hack to force displaying an empty string on nulls, not displaying "null"
// NOTE : we should fix the API instead of doing this, the API shouldn't return null fields !!!
TextField.defaultProps.emptyText = ' ';

// Note : if the JWT token is provided (running in ICX portal), then we won't allow login or logout
// loginPage and logoutButton are undefined in this case
const App = () => {
  return (
    <Admin
      layout={SolsticeLayout}
      theme={SolsticeTheme}
      dataProvider={dataProvider}
      authProvider={authProvider}
      loginPage={isDevMode() && !runningInPortal() ? Login : Empty}
      i18nProvider={i18nProvider}
      logoutButton={isDevMode() && !runningInPortal() && SolsticeLogoutButton}
      customReducers={{ profile: profileReducer }}
      customSagas={[profileSaga]}
    >
      {permissions => {
        if (!permissions.includes('AUTH_ACTIVATE_NEW_ADMIN')) {
          return [<Resource name="notactivated" list={NotActivatedMessage} />];
        }

        return [
          <Resource
            name="users"
            permissions={permissions}
            readPermission="AUTH_READ_USER_NA"
            createPermission="AUTH_CREATE_USER"
            editPermission="AUTH_EDIT_USER"
            deletePermission="AUTH_DELETE_USER"
            list={users.list}
            edit={users.edit}
            create={users.create}
            icon={users.icon}
            options={{ label: 'Contacts' }}
          />,
          <Resource
            name="fundfamilies"
            permissions={permissions}
            readPermission="AUTH_READ_FUND_FAMILY_NA"
            createPermission="AUTH_CREATE_FUND_FAMILY"
            editPermission="AUTH_EDIT_FUND_FAMILY"
            deletePermission="AUTH_DELETE_FUND_FAMILY"
            list={fundfamilies.list}
            edit={fundfamilies.edit}
            create={fundfamilies.create}
            icon={fundfamilies.icon}
            options={{
              label: 'Fund Families',
              group: 'Investment Structure',
              groupIcon: <InvStructureIcon />
            }}
          />,
          <Resource
            name="funds"
            permissions={permissions}
            readPermission="AUTH_READ_FUND_NA"
            createPermission="AUTH_CREATE_FUND"
            editPermission="AUTH_EDIT_FUND"
            deletePermission="AUTH_DELETE_FUND"
            list={funds.list}
            edit={funds.edit}
            create={funds.create}
            icon={funds.icon}
            options={{
              label: 'Funds',
              group: 'Investment Structure',
              groupIcon: <InvStructureIcon />
            }}
          />,
          <Resource
            name="organizations"
            permissions={permissions}
            readPermission="AUTH_READ_INVESTOR_ORGANIZATION_NA"
            createPermission="AUTH_CREATE_INVESTOR_ORGANIZATION"
            editPermission="AUTH_EDIT_INVESTOR_ORGANIZATION"
            deletePermission="AUTH_DELETE_INVESTOR_ORGANIZATION"
            list={organizations.list}
            create={organizations.create}
            edit={organizations.edit}
            icon={organizations.icon}
            options={{
              label: 'Investor Organizations',
              group: 'Investment Structure',
              groupIcon: <InvStructureIcon />
            }}
          />,
          <Resource
            name="investors"
            permissions={permissions}
            readPermission="AUTH_READ_INVESTOR_NA"
            createPermission="AUTH_CREATE_INVESTOR"
            editPermission="AUTH_EDIT_INVESTOR"
            deletePermission="AUTH_DELETE_INVESTOR"
            list={investors.list}
            edit={investors.edit}
            create={investors.create}
            icon={investors.icon}
            options={{
              label: 'Investors',
              group: 'Investment Structure',
              groupIcon: <InvStructureIcon />
            }}
          />,
          <Resource
            name="resourceuploads"
            permissions={permissions}
            readPermission="AUTH_READ_RESOURCE_UPLOAD_NA"
            createPermission="AUTH_CREATE_RESOURCE_UPLOAD"
            editPermission="AUTH_EDIT_RESOURCE_UPLOAD"
            deletePermission="AUTH_DELETE_RESOURCE_UPLOAD"
            list={resourceuploads.list}
            edit={resourceuploads.edit}
            create={resourceuploads.create}
            icon={resourceuploads.icon}
            options={{
              label: 'Document Management'
            }}
          />,
          <Resource
            name="tenantconfigurations"
            permissions={permissions}
            readPermission="AUTH_READ_TENANT_CONFIGURATION_NA"
            createPermission="AUTH_WRITE_TENANT_CONFIGURATION"
            editPermission="AUTH_WRITE_TENANT_CONFIGURATION"
            list={tenantconfigurations.list}
            edit={tenantconfigurations.edit}
            icon={tenantconfigurations.icon}
            options={{
              label: 'Global Configuration',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="splitmethods"
            permissions={permissions}
            readPermission="AUTH_READ_SPLIT_METHOD_NA"
            createPermission="AUTH_CREATE_SPLIT_METHOD"
            editPermission="AUTH_EDIT_SPLIT_METHOD"
            deletePermission="AUTH_DELETE_SPLIT_METHOD"
            list={splitmethods.list}
            create={splitmethods.create}
            edit={splitmethods.edit}
            icon={splitmethods.icon}
            options={{
              label: 'Split Methods',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="categories"
            permissions={permissions}
            readPermission="AUTH_READ_CATEGORY_NA"
            createPermission="AUTH_CREATE_CATEGORY"
            editPermission="AUTH_EDIT_CATEGORY"
            list={categories.list}
            edit={categories.edit}
            create={categories.create}
            icon={categories.icon}
            options={{
              label: 'Document Categories',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="documenttypes"
            permissions={permissions}
            readPermission="AUTH_READ_DOCUMENT_TYPE_NA"
            createPermission="AUTH_WRITE_DOCUMENT_TYPE"
            editPermission="AUTH_WRITE_DOCUMENT_TYPE"
            deletePermission="AUTH_DELETE_DOCUMENT_TYPE"
            list={documenttypes.list}
            create={documenttypes.create}
            edit={documenttypes.edit}
            icon={documenttypes.icon}
            options={{
              label: 'Document Types',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="documentpresets"
            permissions={permissions}
            readPermission="AUTH_READ_DOCUMENT_PRESET_NA"
            createPermission="AUTH_CREATE_DOCUMENT_PRESET"
            editPermission="AUTH_EDIT_DOCUMENT_PRESET"
            deletePermission="AUTH_DELETE_DOCUMENT_PRESET"
            list={presets.list}
            edit={presets.edit}
            create={presets.create}
            icon={presets.icon}
            options={{
              label: 'Document Profiles',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="documentwatermarks"
            permissions={permissions}
            readPermission="AUTH_READ_DOCUMENT_WATERMARK_NA"
            createPermission="AUTH_CREATE_DOCUMENT_WATERMARK"
            editPermission="AUTH_EDIT_DOCUMENT_WATERMARK"
            deletePermission="AUTH_DELETE_DOCUMENT_WATERMARK"
            list={documentwatermarks.list}
            create={documentwatermarks.create}
            edit={documentwatermarks.edit}
            icon={documentwatermarks.icon}
            options={{
              label: 'Watermark Profiles',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="groups"
            permissions={permissions}
            readPermission="AUTH_READ_GROUP_NA"
            createPermission="AUTH_CREATE_GROUP"
            editPermission="AUTH_EDIT_GROUP"
            list={groups.list}
            create={groups.create}
            edit={groups.edit}
            icon={groups.icon}
            options={{
              label: 'Permission Groups',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="roles"
            permissions={permissions}
            readPermission="AUTH_READ_ROLE_NA"
            createPermission="AUTH_CREATE_ROLE"
            editPermission="AUTH_EDIT_ROLE"
            deletePermission="AUTH_DELETE_ROLE"
            list={roles.list}
            create={roles.create}
            edit={roles.edit}
            icon={roles.icon}
            options={{
              label: 'Access Roles',
              group: 'Global Settings',
              groupIcon: <SettingsIcon />
            }}
          />,
          <Resource
            name="fundtags"
            permissions={permissions}
            readPermission="AUTH_READ_FUND_TAG_NA"
            createPermission="AUTH_CREATE_FUND_TAG"
            editPermission="AUTH_EDIT_FUND_TAG"
            deletePermission="AUTH_DELETE_FUND_TAG"
            list={fundtags.list}
            create={fundtags.create}
            edit={fundtags.edit}
            icon={fundtags.icon}
            options={{
              label: 'Fund Tags',
              group: 'Tags',
              groupIcon: <TagIcon />
            }}
          />,
          <Resource
            name="investortags"
            permissions={permissions}
            readPermission="AUTH_READ_INVESTOR_TAG_NA"
            createPermission="AUTH_CREATE_INVESTOR_TAG"
            editPermission="AUTH_EDIT_INVESTOR_TAG"
            deletePermission="AUTH_DELETE_INVESTOR_TAG"
            list={investortags.list}
            create={investortags.create}
            edit={investortags.edit}
            icon={investortags.icon}
            options={{
              label: 'Investor Tags',
              group: 'Tags',
              groupIcon: <TagIcon />
            }}
          />,
          <Resource
            name="fundinvestortags"
            permissions={permissions}
            readPermission="AUTH_READ_FUND_INVESTOR_TAG_NA"
            createPermission="AUTH_CREATE_FUND_INVESTOR_TAG"
            editPermission="AUTH_EDIT_FUND_INVESTOR_TAG"
            deletePermission="AUTH_DELETE_FUND_INVESTOR_TAG"
            list={fundinvestortags.list}
            create={fundinvestortags.create}
            edit={fundinvestortags.edit}
            icon={fundinvestortags.icon}
            options={{
              label: 'Fund-Investor Tags',
              group: 'Tags',
              groupIcon: <TagIcon />
            }}
          />,
          <Resource
            name="fundterms"
            permissions={permissions}
            readPermission="AUTH_READ_FUND_TERM_AND_CONDITION_NA"
            createPermission="AUTH_CREATE_FUND_TERM_AND_CONDITION"
            editPermission="AUTH_EDIT_FUND_TERM_AND_CONDITION"
            list={fundterms.list}
            create={fundterms.create}
            edit={fundterms.edit}
            icon={fundterms.icon}
            options={{
              label: 'Fund Terms',
              group: 'Terms and Conditions',
              groupIcon: <TermsIcon />
            }}
          />,
          <Resource
            name="categoryterms"
            permissions={permissions}
            readPermission="AUTH_READ_CATEGORY_TERM_AND_CONDITION_NA"
            createPermission="AUTH_CREATE_CATEGORY_TERM_AND_CONDITION"
            editPermission="AUTH_EDIT_CATEGORY_TERM_AND_CONDITION"
            list={categoryterms.list}
            create={categoryterms.create}
            edit={categoryterms.edit}
            icon={categoryterms.icon}
            options={{
              label: 'Category Terms',
              group: 'Terms and Conditions',
              groupIcon: <TermsIcon />
            }}
          />,
          <Resource
            name="mailtemplates"
            permissions={permissions}
            readPermission="AUTH_READ_MAIL_TEMPLATE_NA"
            createPermission="AUTH_CREATE_MAIL_TEMPLATE"
            editPermission="AUTH_EDIT_MAIL_TEMPLATE"
            deletePermission="AUTH_DELETE_MAIL_TEMPLATE"
            list={mailtemplates.list}
            create={mailtemplates.create}
            edit={mailtemplates.edit}
            icon={mailtemplates.icon}
            options={{
              label: 'Email Templates',
              group: 'Email Settings',
              groupIcon: <AlternateEmail />
            }}
          />,
          <Resource
            name="distributionlists"
            permissions={permissions}
            readPermission="AUTH_READ_DISTRIBUTION_LIST"
            createPermission="AUTH_CREATE_DISTRIBUTION_LIST"
            editPermission="AUTH_EDIT_DISTRIBUTION_LIST"
            deletePermission="AUTH_DELETE_DISTRIBUTION_LIST"
            list={distributionlists.list}
            show={distributionlists.show}
            create={distributionlists.create}
            icon={distributionlists.icon}
            options={{
              label: 'Distribution Lists',
              group: 'Email Settings',
              groupIcon: <AlternateEmail />
            }}
          />,
          <Resource
            name="reports"
            permissions={permissions}
            readPermission="AUTH_READ_ADMIN_REPORT"
            editPermission="AUTH_READ_ADMIN_REPORT"
            list={reports.list}
            edit={reports.edit}
            icon={reports.icon}
            options={{
              label: 'Administrative Reports'
            }}
          />,
          <Resource
            name="emailarchives"
            list={emailarchive.list}
            show={emailarchive.show}
            icon={emailarchive.icon}
            permissions={permissions}
            readPermission="AUTH_READ_EMAIL_ARCHIVE"
            editPermission="AUTH_READ_EMAIL_ARCHIVE"
            options={{
              label: 'Email Archive',
              group: 'Email Settings',
              groupIcon: <AlternateEmailIcon />
            }}
          />,
          <Resource name="categorytermsrelations" />,
          <Resource name="fundtermsrelations" />,
          <Resource name="authorities" />,
          <Resource name="userphones" />,
          <Resource name="useraddresses" />,
          <Resource name="userphonetypes" />,
          <Resource name="useraddresstypes" />,
          <Resource name="userauthorizations" />,
          <Resource name="userroles" />,
          <Resource name="subcategories" />,
          <Resource name="fundclasses" />,
          <Resource name="fundinvestors" />,
          <Resource name="groupcategories" />,
          <Resource name="countries" />,
          <Resource name="states" />,
          <Resource name="documentsecurities" />,
          <Resource name="categorytypes" />,
          <Resource name="currencies" />,
          <Resource name="documents" />,
          <Resource name="emailarchiveattachments" />,
          <Resource name="emailarchiverecipients" />,
          <Resource name="distributionlistrecipients" />,
          <Resource name="mfamethods" />,
          <Resource name="investorcontacts" />,
          <Resource name="usernotifications" />,
          <Resource name="activities" />,
          <Resource name="userudfs" />,
          <Resource name="wireinstructions" />,
          <Resource name="investorudfs" />,
          <Resource name="fundinvestorudfs" />,
          <Resource name="primarycontacts" />,
          <Resource name="investorcount" />,
          <Resource name="fundtagsassociations" />,
          <Resource name="investortagsassociations" />,
          <Resource name="fundinvestortagsassociations" />
        ];
      }}
    </Admin>
  );
};

export default App;
